import React, { useState, useEffect, useMemo } from 'react';
import { 
  ShoppingBag, 
  DollarSign, 
  Users, 
  Store,
  Truck,
  AlertTriangle,
  Clock,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  BarChart3,
  ChevronRight,
  Loader2
} from 'lucide-react';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { cn } from '../../lib/utils';

// ─── Helpers ────────────────────────────────────────────────

function toDate(value: any): Date {
  if (!value) return new Date(0);
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
}

function timeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Buckets used for the status chart and recent-order pills. */
function stateBucket(state: string): string {
  if (['CREATED', 'PRICED', 'CART_VALIDATED', 'PAYMENT_AUTHORIZED', 'PENDING_BUSINESS_APPROVAL'].includes(state)) return 'pending';
  if (['BUSINESS_APPROVED', 'DISPATCHING_DRIVER', 'DRIVER_ASSIGNED'].includes(state)) return 'approved';
  if (['PICKED_UP', 'OUT_FOR_DELIVERY'].includes(state)) return 'picked_up';
  if (['DELIVERED', 'COMPLETED'].includes(state)) return 'delivered';
  if (state === 'DISPUTED') return 'disputed';
  if (['CANCELLED', 'REFUNDED', 'PAYMENT_FAILED', 'BUSINESS_REJECTED'].includes(state)) return 'cancelled';
  return 'pending';
}

// States where payment has been captured (counted as revenue)
const REVENUE_STATES = ['PICKED_UP', 'OUT_FOR_DELIVERY', 'DELIVERED', 'COMPLETED'];

interface OrderDoc {
  id: string;
  customerName: string;
  businessName: string;
  businessId: string;
  total: number;
  state: string;
  createdAt: Date;
  updatedAt: Date;
}

// ─── Live data hook ─────────────────────────────────────────

function useAdminDashboardData() {
  const [orders, setOrders] = useState<OrderDoc[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [userCount, setUserCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'), limit(250));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setOrders(
          snap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              customerName: data.customerName || 'Customer',
              businessName: data.businessName || data.businessId || 'Boutique',
              businessId: data.businessId || '',
              total: data.total || 0,
              state: data.state || 'CREATED',
              createdAt: toDate(data.createdAt),
              updatedAt: toDate(data.updatedAt),
            };
          })
        );
        setLoading(false);
      },
      (err) => {
        console.error('[AdminDashboard] orders subscription error:', err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'drivers'), limit(100)),
      (snap) => setDrivers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error('[AdminDashboard] drivers subscription error:', err)
    );
    return unsub;
  }, []);

  useEffect(() => {
    getCountFromServer(collection(db, 'users'))
      .then((snap) => setUserCount(snap.data().count))
      .catch((err) => console.error('[AdminDashboard] user count error:', err));
  }, []);

  return { orders, drivers, userCount, loading };
}

// ─── Presentational components ──────────────────────────────

interface StatCardProps {
  title: string;
  value: string | number;
  change: number;
  changeLabel: string;
  icon: React.ReactNode;
  trend: 'up' | 'down' | 'neutral';
  color: string;
}

function StatCard({ title, value, change, changeLabel, icon, trend, color }: StatCardProps) {
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-3 sm:p-4 lg:p-6">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm font-medium text-body truncate">{title}</p>
            <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-heading mt-1 sm:mt-2">{value}</p>
            <div className="flex items-center mt-1 sm:mt-2 space-x-0.5 sm:space-x-1 flex-wrap">
              {trend === 'up' ? (
                <ArrowUpRight className="h-3 w-3 sm:h-4 sm:w-4 text-success flex-shrink-0" />
              ) : trend === 'down' ? (
                <ArrowDownRight className="h-3 w-3 sm:h-4 sm:w-4 text-error flex-shrink-0" />
              ) : null}
              <span className={cn(
                "text-xs sm:text-sm font-medium",
                trend === 'up' ? "text-success" : trend === 'down' ? "text-error" : "text-body"
              )}>
                {trend === 'neutral' ? '—' : `${change > 0 ? '+' : ''}${change}%`}
              </span>
              <span className="text-xs sm:text-sm text-body hidden sm:inline">{changeLabel}</span>
            </div>
          </div>
          <div className={cn("p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0", color)}>
            {React.cloneElement(icon as React.ReactElement, {
              className: "h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-white"
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface RecentOrderProps {
  id: string;
  customer: string;
  boutique: string;
  amount: number;
  status: string;
  time: string;
}

function RecentOrderRow({ customer, boutique, amount, status, time }: RecentOrderProps) {
  const statusStyles: Record<string, string> = {
    'pending': 'bg-amber-100 text-amber-700',
    'approved': 'bg-blue-100 text-blue-700',
    'picked_up': 'bg-purple-100 text-purple-700',
    'delivered': 'bg-green-100 text-green-700',
    'disputed': 'bg-red-100 text-red-700',
    'cancelled': 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="flex items-center justify-between py-3 sm:py-4 border-b border-gray-100 last:border-0 gap-3">
      <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-medium text-xs sm:text-sm flex-shrink-0">
          {customer.charAt(0)}
        </div>
        <div className="min-w-0">
          <p className="font-medium text-heading text-sm sm:text-base truncate">{customer}</p>
          <p className="text-xs sm:text-sm text-body truncate">{boutique}</p>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="font-semibold text-heading text-sm sm:text-base">${amount.toFixed(2)}</p>
        <div className="flex items-center justify-end space-x-1 sm:space-x-2 mt-1 flex-wrap">
          <span className={cn("px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium capitalize", statusStyles[status] || 'bg-gray-100 text-gray-700')}>
            {status.replace('_', ' ')}
          </span>
          <span className="text-[10px] sm:text-xs text-body">{time}</span>
        </div>
      </div>
    </div>
  );
}

interface ActivityItem {
  id: string;
  type: 'order' | 'user' | 'dispute' | 'driver' | 'business';
  message: string;
  time: string;
}

function ActivityFeed({ activities }: { activities: ActivityItem[] }) {
  const icons: Record<string, React.ReactNode> = {
    order: <Package className="h-3 w-3 sm:h-4 sm:w-4" />,
    user: <Users className="h-3 w-3 sm:h-4 sm:w-4" />,
    dispute: <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4" />,
    driver: <Truck className="h-3 w-3 sm:h-4 sm:w-4" />,
    business: <Store className="h-3 w-3 sm:h-4 sm:w-4" />,
  };

  const colors: Record<string, string> = {
    order: 'bg-purple-100 text-purple-600',
    user: 'bg-blue-100 text-blue-600',
    dispute: 'bg-red-100 text-red-600',
    driver: 'bg-green-100 text-green-600',
    business: 'bg-amber-100 text-amber-600',
  };

  if (activities.length === 0) {
    return <p className="text-sm text-body py-4 text-center">No recent activity yet</p>;
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {activities.map((activity) => (
        <div key={activity.id} className="flex items-start space-x-2 sm:space-x-3">
          <div className={cn("p-1.5 sm:p-2 rounded-lg flex-shrink-0", colors[activity.type])}>
            {icons[activity.type]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm text-heading line-clamp-2">{activity.message}</p>
            <p className="text-[10px] sm:text-xs text-body mt-0.5 sm:mt-1">{activity.time}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────

export function AdminDashboard({ onNavigate }: { onNavigate?: (page: string) => void }) {
  const { orders, drivers, userCount, loading } = useAdminDashboardData();

  const computed = useMemo(() => {
    const now = Date.now();
    const dayMs = 86400000;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfYesterday = new Date(startOfToday.getTime() - dayMs);

    // Revenue: captured-payment orders over the trailing 30 days
    const revenueCurrent = orders
      .filter((o) => REVENUE_STATES.includes(o.state) && now - o.createdAt.getTime() < 30 * dayMs)
      .reduce((sum, o) => sum + o.total, 0);
    const revenuePrev = orders
      .filter((o) => {
        const age = now - o.createdAt.getTime();
        return REVENUE_STATES.includes(o.state) && age >= 30 * dayMs && age < 60 * dayMs;
      })
      .reduce((sum, o) => sum + o.total, 0);
    const revenueChange = revenuePrev > 0
      ? Math.round(((revenueCurrent - revenuePrev) / revenuePrev) * 100)
      : 0;

    const ordersToday = orders.filter((o) => o.createdAt >= startOfToday).length;
    const ordersYesterday = orders.filter(
      (o) => o.createdAt >= startOfYesterday && o.createdAt < startOfToday
    ).length;
    const ordersChange = ordersYesterday > 0
      ? Math.round(((ordersToday - ordersYesterday) / ordersYesterday) * 100)
      : 0;

    const openDisputes = orders.filter((o) => o.state === 'DISPUTED').length;

    // Status breakdown for active (non-terminal) orders
    const buckets = { pending: 0, approved: 0, picked_up: 0, delivered: 0 };
    orders.forEach((o) => {
      const bucket = stateBucket(o.state);
      if (bucket in buckets) (buckets as any)[bucket]++;
    });

    const recentOrders: RecentOrderProps[] = orders.slice(0, 5).map((o) => ({
      id: o.id,
      customer: o.customerName,
      boutique: o.businessName,
      amount: o.total,
      status: stateBucket(o.state),
      time: timeAgo(o.createdAt),
    }));

    // Activity feed: most recently updated orders
    const activities: ActivityItem[] = [...orders]
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
      .slice(0, 6)
      .map((o) => {
        const bucket = stateBucket(o.state);
        const type: ActivityItem['type'] =
          bucket === 'disputed' ? 'dispute' :
          bucket === 'picked_up' ? 'driver' :
          'order';
        return {
          id: o.id,
          type,
          message: `Order #${o.id.slice(-6).toUpperCase()} (${o.businessName}) → ${o.state.replace(/_/g, ' ').toLowerCase()}`,
          time: timeAgo(o.updatedAt),
        };
      });

    // Top boutiques by captured revenue
    const byBusiness = new Map<string, { name: string; orders: number; revenue: number }>();
    orders.forEach((o) => {
      if (!REVENUE_STATES.includes(o.state)) return;
      const entry = byBusiness.get(o.businessId) || { name: o.businessName, orders: 0, revenue: 0 };
      entry.orders += 1;
      entry.revenue += o.total;
      byBusiness.set(o.businessId, entry);
    });
    const topBoutiques = [...byBusiness.values()]
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 4);

    const topDrivers = [...drivers]
      .filter((d) => (d.stats?.deliveries || 0) > 0)
      .sort((a, b) => (b.stats?.deliveries || 0) - (a.stats?.deliveries || 0))
      .slice(0, 4)
      .map((d) => ({
        name: d.name || d.displayName || 'Driver',
        deliveries: d.stats?.deliveries || 0,
        earnings: (d.stats?.earningsCents || 0) / 100,
        online: d.status === 'online' || d.status === 'busy',
      }));

    return {
      revenueCurrent,
      revenueChange,
      ordersToday,
      ordersChange,
      openDisputes,
      buckets,
      recentOrders,
      activities,
      topBoutiques,
      topDrivers,
    };
  }, [orders, drivers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  const stats: StatCardProps[] = [
    {
      title: 'Revenue (30d)',
      value: `$${computed.revenueCurrent.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
      change: computed.revenueChange,
      changeLabel: 'vs prior 30d',
      icon: <DollarSign className="h-6 w-6 text-white" />,
      trend: computed.revenueChange > 0 ? 'up' : computed.revenueChange < 0 ? 'down' : 'neutral',
      color: 'bg-gradient-to-br from-green-400 to-emerald-500',
    },
    {
      title: 'Orders Today',
      value: computed.ordersToday,
      change: computed.ordersChange,
      changeLabel: 'vs yesterday',
      icon: <ShoppingBag className="h-6 w-6 text-white" />,
      trend: computed.ordersChange > 0 ? 'up' : computed.ordersChange < 0 ? 'down' : 'neutral',
      color: 'bg-gradient-to-br from-purple-400 to-indigo-500',
    },
    {
      title: 'Registered Users',
      value: userCount !== null ? userCount.toLocaleString() : '…',
      change: 0,
      changeLabel: 'all time',
      icon: <Users className="h-6 w-6 text-white" />,
      trend: 'neutral',
      color: 'bg-gradient-to-br from-blue-400 to-cyan-500',
    },
    {
      title: 'Open Disputes',
      value: computed.openDisputes,
      change: 0,
      changeLabel: 'needs attention',
      icon: <AlertTriangle className="h-6 w-6 text-white" />,
      trend: 'neutral',
      color: 'bg-gradient-to-br from-red-400 to-pink-500',
    },
  ];

  const totalBucketed = Object.values(computed.buckets).reduce((a, b) => a + b, 0) || 1;
  const orderStatusBreakdown = [
    { label: 'Pending', count: computed.buckets.pending, color: 'bg-amber-500' },
    { label: 'Approved', count: computed.buckets.approved, color: 'bg-blue-500' },
    { label: 'In Transit', count: computed.buckets.picked_up, color: 'bg-purple-500' },
    { label: 'Delivered', count: computed.buckets.delivered, color: 'bg-green-500' },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif text-heading">Dashboard</h1>
          <p className="text-body text-sm sm:text-base mt-0.5 sm:mt-1">Welcome back! Here's what's happening today.</p>
        </div>
        <div className="flex items-center space-x-2 text-xs sm:text-sm">
          <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-success animate-pulse" />
          <span className="text-body">Live</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {stats.map((stat, idx) => (
          <StatCard key={idx} {...stat} />
        ))}
      </div>

      {/* Business Analytics Link */}
      <button
        onClick={() => onNavigate?.('analytics')}
        className="w-full group"
      >
        <Card className="overflow-hidden border-purple-100 hover:border-purple-300 hover:shadow-md transition-all">
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-purple-50 to-indigo-50">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="p-2.5 sm:p-3 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shadow-purple-200">
                  <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-sm sm:text-base font-semibold text-heading">Business Analytics Dashboard</h3>
                  <p className="text-xs sm:text-sm text-body mt-0.5">
                    Revenue trends, product performance, customer insights, heatmaps & more
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-purple-400 group-hover:translate-x-1 transition-transform flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
      </button>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Recent Orders */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 p-3 sm:p-4 lg:p-6">
            <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
              <ShoppingBag className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              <span>Recent Orders</span>
            </CardTitle>
            <button
              onClick={() => onNavigate?.('orders')}
              className="text-xs sm:text-sm text-primary font-medium hover:underline"
            >
              View All →
            </button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="p-3 sm:p-4">
              {computed.recentOrders.length === 0 ? (
                <p className="text-sm text-body py-6 text-center">No orders yet</p>
              ) : (
                computed.recentOrders.map((order) => (
                  <RecentOrderRow key={order.id} {...order} />
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Activity Feed */}
        <Card>
          <CardHeader className="border-b border-gray-100 p-3 sm:p-4 lg:p-6">
            <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              <span>Recent Activity</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4">
            <ActivityFeed activities={computed.activities} />
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Order Status Breakdown */}
        <Card>
          <CardHeader className="border-b border-gray-100 p-3 sm:p-4 lg:p-6">
            <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
              <Package className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              <span>Order Status</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4">
            <div className="space-y-3 sm:space-y-4">
              {orderStatusBreakdown.map((status) => (
                <div key={status.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs sm:text-sm text-body">{status.label}</span>
                    <span className="text-xs sm:text-sm font-medium text-heading">{status.count}</span>
                  </div>
                  <div className="h-1.5 sm:h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className={cn("h-full rounded-full transition-all", status.color)}
                      style={{ width: `${(status.count / totalBucketed) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Boutiques */}
        <Card>
          <CardHeader className="border-b border-gray-100 p-3 sm:p-4 lg:p-6">
            <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
              <Store className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              <span>Top Boutiques</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4">
            {computed.topBoutiques.length === 0 ? (
              <p className="text-sm text-body py-6 text-center">No completed orders yet</p>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {computed.topBoutiques.map((boutique, idx) => (
                  <div key={boutique.name + idx} className="flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                      <span className={cn(
                        "w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold flex-shrink-0",
                        idx === 0 ? "bg-amber-100 text-amber-700" :
                        idx === 1 ? "bg-gray-200 text-gray-600" :
                        idx === 2 ? "bg-orange-100 text-orange-600" :
                        "bg-gray-100 text-gray-500"
                      )}>
                        {idx + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-heading truncate">{boutique.name}</p>
                        <p className="text-[10px] sm:text-xs text-body">{boutique.orders} orders</p>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm font-semibold text-heading flex-shrink-0">
                      ${boutique.revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Drivers */}
        <Card className="sm:col-span-2 lg:col-span-1">
          <CardHeader className="border-b border-gray-100 p-3 sm:p-4 lg:p-6">
            <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
              <Truck className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              <span>Top Drivers</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4">
            {computed.topDrivers.length === 0 ? (
              <p className="text-sm text-body py-6 text-center">No completed deliveries yet</p>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {computed.topDrivers.map((driver) => (
                  <div key={driver.name} className="flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                      <div className="relative flex-shrink-0">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white text-[10px] sm:text-xs font-bold">
                          {driver.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                        </div>
                        {driver.online && (
                          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-heading truncate">{driver.name}</p>
                        <p className="text-[10px] sm:text-xs text-body">{driver.deliveries} deliveries</p>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm font-semibold text-heading flex-shrink-0">
                      ${driver.earnings.toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
